import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  ResponsiveContainer, ComposedChart, Area, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList,
} from 'recharts';
import {
  FileSpreadsheet, Upload, Search, ArrowUpRight, ArrowDownRight, Minus,
  BarChart3, Table as TableIcon, Download, X,
  LineChart as LineChartIcon, AreaChart as AreaChartIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { SalesImportDialog } from '@/components/SalesImportDialog';
import { SellThroughPanel } from '@/components/SellThroughPanel';
import {
  fetchSalesEntries, fetchSalesDateBounds, fetchAssemblyOrderIndex, fetchSalesStores, serialKey,
  type AssemblyOrderRef,
} from '@/lib/api/salesEntries';
import { formatCurrency, cn } from '@/lib/utils';
import {
  RANGE_PRESETS, presetRange, suggestGranularity, comparisonRange, comparisonLabel,
  aggregate, pctDelta, buildSeries, breakdown, modelLabel, familyLabel,
  repLabel, formatISODate, isValidISODate, todayISO, daysInclusive, OTHER_KEY, resolveStore,
  type RangePreset, type Granularity, type ComparisonMode, type SalesRow,
  type DateRange, type SeriesPoint, type Breakdown, type ResolvedStore, type SalesStore,
} from '@/lib/salesAnalytics';

/** Dropdown order follows the BC dimension tree; unmapped codes go last. */
const STORE_GROUP_ORDER = ['Retail', 'B2B', 'POS', 'Other'];

function storeDisplay(s: ResolvedStore): string {
  return s.closed ? `${s.name} (closed)` : s.name;
}

type Measure = 'revenue' | 'units' | 'margin';

type TrendChartType = 'line' | 'bar' | 'area';

const MEASURES: { value: Measure; label: string }[] = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'units', label: 'Units' },
  { value: 'margin', label: 'Gross margin' },
];

const GRANULARITIES: { value: 'auto' | Granularity; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'quarter', label: 'Quarterly' },
  { value: 'year', label: 'Yearly' },
];

const COMPARISONS: { value: ComparisonMode; label: string }[] = [
  { value: 'prev', label: 'vs previous period' },
  { value: 'yoy', label: 'vs last year' },
  { value: 'none', label: 'No comparison' },
];

const SELECT_CLASS = 'h-9 px-2 rounded-md border border-input bg-background text-sm';

/**
 * How the report is framed — period, comparison, measure, chart — survives a
 * reload. The dimension filters (store, model, seller…) deliberately don't: a
 * forgotten pill would quietly narrow every figure the next time it's opened.
 */
const VIEW_KEY = 'salesReport.view';

interface SavedView {
  preset: RangePreset;
  customFrom: string;
  customTo: string;
  granularity: 'auto' | Granularity;
  compare: ComparisonMode;
  measure: Measure;
  trendView: 'chart' | 'table';
  trendChartType: TrendChartType | null;
}

function loadView(): Partial<SavedView> {
  try {
    const raw = JSON.parse(localStorage.getItem(VIEW_KEY) || '{}');
    // Each field is checked against its option list, so a renamed option or a
    // hand-edited value falls back to the default instead of breaking the page.
    const pick = <T,>(v: unknown, allowed: readonly T[]): T | undefined =>
      allowed.includes(v as T) ? (v as T) : undefined;
    return {
      preset: pick(raw.preset, RANGE_PRESETS.map(p => p.value)),
      customFrom: isValidISODate(raw.customFrom) ? raw.customFrom : undefined,
      customTo: isValidISODate(raw.customTo) ? raw.customTo : undefined,
      granularity: pick(raw.granularity, GRANULARITIES.map(g => g.value)),
      compare: pick(raw.compare, COMPARISONS.map(c => c.value)),
      measure: pick(raw.measure, MEASURES.map(m => m.value)),
      trendView: pick(raw.trendView, ['chart', 'table'] as const),
      trendChartType: pick(raw.trendChartType, ['line', 'bar', 'area'] as const),
    };
  } catch {
    return {};
  }
}

function compactCurrency(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}€${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}€${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `${sign}€${abs.toFixed(0)}`;
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

export function SalesReportPage() {
  const [importOpen, setImportOpen] = useState(false);

  // ── Filters (one row, scoping everything below) ────────────────────────────
  const [saved] = useState(loadView);
  const [preset, setPreset] = useState<RangePreset>(saved.preset ?? 'last_12m');
  const [customFrom, setCustomFrom] = useState(saved.customFrom ?? '');
  const [customTo, setCustomTo] = useState(saved.customTo ?? '');
  const [granularity, setGranularity] = useState<'auto' | Granularity>(saved.granularity ?? 'auto');
  const [compare, setCompare] = useState<ComparisonMode>(saved.compare ?? 'prev');
  const [measure, setMeasure] = useState<Measure>(saved.measure ?? 'revenue');
  const [storeFilter, setStoreFilter] = useState('');
  const [familyFilter, setFamilyFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [repFilter, setRepFilter] = useState('');
  const [search, setSearch] = useState('');

  // The two halves of the page answer different questions off different tables,
  // so they are tabs rather than one long scroll. Only the date range is shared
  // — see the note where the dimension filters are hidden.
  const [tab, setTab] = useState<'sales' | 'sellThrough'>('sales');

  const [trendView, setTrendView] = useState<'chart' | 'table'>(saved.trendView ?? 'chart');
  // null = follow the default for the current comparison state; a click pins a choice.
  const [trendChartType, setTrendChartType] = useState<TrendChartType | null>(saved.trendChartType ?? null);
  const [tablePage, setTablePage] = useState(1);

  useEffect(() => {
    const view: SavedView = {
      preset, customFrom, customTo, granularity, compare, measure, trendView, trendChartType,
    };
    try { localStorage.setItem(VIEW_KEY, JSON.stringify(view)); } catch {}
  }, [preset, customFrom, customTo, granularity, compare, measure, trendView, trendChartType]);

  const { data: bounds } = useQuery({
    queryKey: ['sales-date-bounds'],
    queryFn: fetchSalesDateBounds,
    staleTime: 5 * 60 * 1000,
  });

  const range: DateRange = useMemo(() => {
    if (preset === 'custom') {
      const from = isValidISODate(customFrom) ? customFrom : presetRange('ytd', bounds?.earliest ?? null).from;
      const to = isValidISODate(customTo) ? customTo : todayISO();
      return from <= to ? { from, to } : { from: to, to: from };
    }
    return presetRange(preset, bounds?.earliest ?? null);
  }, [preset, customFrom, customTo, bounds?.earliest]);

  const effectiveGranularity: Granularity =
    granularity === 'auto' ? suggestGranularity(range) : granularity;

  const requestedCompRange = useMemo(() => comparisonRange(range, compare), [range, compare]);

  // A comparison window that ends before the first entry on record would plot a
  // flat zero line — asserting "nothing sold" when the truth is "nothing
  // imported". Drop the comparison instead of drawing a lie.
  const comparisonUnavailable =
    !!requestedCompRange && !!bounds?.earliest && requestedCompRange.to < bounds.earliest;
  const compRange = comparisonUnavailable ? null : requestedCompRange;

  const defaultTrendChart: TrendChartType = compRange ? 'line' : 'area';

  // One query covering both periods — the volumes here are small enough that a
  // single fetch beats two round trips.
  const fetchRange: DateRange = useMemo(() => ({
    from: compRange && compRange.from < range.from ? compRange.from : range.from,
    to: compRange && compRange.to > range.to ? compRange.to : range.to,
  }), [range, compRange]);

  const { data: allRows, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['sales-entries', fetchRange.from, fetchRange.to],
    queryFn: () => fetchSalesEntries(fetchRange),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  const rows = allRows ?? [];

  // Scoped to the same range as the entries, so the join returns only the orders
  // that could match rather than the whole table.
  const { data: asoIndex } = useQuery({
    queryKey: ['assembly-order-index', fetchRange.from, fetchRange.to],
    queryFn: () => fetchAssemblyOrderIndex(fetchRange),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const lookupAso = useMemo(() => {
    return (serial: string | null): AssemblyOrderRef | null => {
      const key = serialKey(serial);
      if (!key || !asoIndex) return null;
      return asoIndex.bySerial.get(key) ?? null;
    };
  }, [asoIndex]);

  const trackingFrom = asoIndex?.trackingFrom ?? null;

  // If this fails the report still works — every store just shows its raw code.
  const { data: storeMap } = useQuery({
    queryKey: ['sales-stores'],
    queryFn: fetchSalesStores,
    staleTime: 30 * 60 * 1000,
  });

  // Grouped on the store's name, not its code: BC has renumbered stores, and a
  // retired code should add to its store's history rather than stand apart.
  const storeOf = useMemo(() => {
    const map = storeMap ?? new Map<string, SalesStore>();
    return (r: SalesRow) => resolveStore(r.store_code, map);
  }, [storeMap]);

  // Filter option lists come from the unfiltered current-period rows, so a
  // filter never removes its own option from the list.
  const inRange = useMemo(
    () => rows.filter(r => r.posting_date >= range.from && r.posting_date <= range.to),
    [rows, range],
  );

  const options = useMemo(() => {
    const stores = new Map<string, ResolvedStore>();
    const families = new Set<string>();
    const models = new Map<string, string>();
    const reps = new Set<string>();
    for (const r of inRange) {
      const s = storeOf(r);
      if (!stores.has(s.name)) stores.set(s.name, s);
      if (r.family) families.add(r.family);
      if (r.created_by) reps.add(r.created_by);
      if (!models.has(r.item_no)) models.set(r.item_no, r.item_no);
    }
    for (const [itemNo] of models) {
      models.set(itemNo, modelLabel(itemNo, inRange.filter(r => r.item_no === itemNo)));
    }
    const storeGroups = new Map<string, ResolvedStore[]>();
    for (const s of stores.values()) {
      const g = s.group ?? 'Unmapped';
      storeGroups.set(g, [...(storeGroups.get(g) ?? []), s]);
    }
    const rank = (g: string) => {
      const i = STORE_GROUP_ORDER.indexOf(g);
      return i === -1 ? STORE_GROUP_ORDER.length : i;
    };
    return {
      storeGroups: [...storeGroups.entries()]
        .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
        .map(([group, list]) => ({ group, stores: list.sort((a, b) => a.name.localeCompare(b.name)) })),
      families: [...families].sort(),
      models: [...models.entries()].sort((a, b) => a[1].localeCompare(b[1])),
      reps: [...reps].sort(),
    };
  }, [inRange, storeOf]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (r: SalesRow) => {
      if (storeFilter && storeOf(r).name !== storeFilter) return false;
      if (familyFilter && r.family !== familyFilter) return false;
      if (modelFilter && r.item_no !== modelFilter) return false;
      if (channelFilter && r.channel !== channelFilter) return false;
      if (repFilter && r.created_by !== repFilter) return false;
      if (q) {
        const hay = [
          r.serial_no, r.document_no, r.item_no, r.description, r.created_by,
          lookupAso(r.serial_no)?.order_number,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    };
  }, [storeFilter, familyFilter, modelFilter, channelFilter, repFilter, search, lookupAso, storeOf]);

  const currentRows = useMemo(() => inRange.filter(matches), [inRange, matches]);

  // Surfaced above the table so the size of the gap is visible rather than
  // implied by a column full of dashes.
  const linkStats = useMemo(() => {
    let linked = 0, noSerial = 0, unmatched = 0, preTracking = 0;
    for (const r of currentRows) {
      if (!serialKey(r.serial_no)) noSerial++;
      else if (lookupAso(r.serial_no)) linked++;
      else if (trackingFrom && r.posting_date < trackingFrom) preTracking++;
      else unmatched++;
    }
    return { linked, noSerial, unmatched, preTracking };
  }, [currentRows, lookupAso, trackingFrom]);

  const priorRows = useMemo(() => {
    if (!compRange) return [];
    return rows.filter(r => r.posting_date >= compRange.from && r.posting_date <= compRange.to).filter(matches);
  }, [rows, compRange, matches]);

  const totals = useMemo(() => aggregate(currentRows), [currentRows]);
  const priorTotals = useMemo(() => aggregate(priorRows), [priorRows]);

  const series = useMemo(
    () => buildSeries(currentRows, range, effectiveGranularity, compRange ? priorRows : undefined, compRange),
    [currentRows, range, effectiveGranularity, priorRows, compRange],
  );

  // Each dimension is computed twice: capped at 8 with an "Other" tail for the
  // card, and uncapped for the "show all" dialog behind it.
  const TOP_N = 8;
  const byModel = useMemo(
    () => breakdown(currentRows, r => r.item_no, (k) => modelLabel(k, currentRows.filter(r => r.item_no === k)), measure, TOP_N),
    [currentRows, measure],
  );
  const byModelAll = useMemo(
    () => breakdown(currentRows, r => r.item_no, (k) => modelLabel(k, currentRows.filter(r => r.item_no === k)), measure),
    [currentRows, measure],
  );
  // Labels need the resolved store (for the closed marker), keyed by name.
  const storeLabel = useMemo(() => {
    const byName = new Map(options.storeGroups.flatMap(g => g.stores).map(s => [s.name, storeDisplay(s)]));
    return (name: string) => byName.get(name) ?? name;
  }, [options.storeGroups]);
  const byStore = useMemo(
    () => breakdown(currentRows, r => storeOf(r).name, storeLabel, measure, TOP_N),
    [currentRows, measure, storeOf, storeLabel],
  );
  const byStoreAll = useMemo(
    () => breakdown(currentRows, r => storeOf(r).name, storeLabel, measure),
    [currentRows, measure, storeOf, storeLabel],
  );
  const byRep = useMemo(
    () => breakdown(currentRows, r => r.created_by ?? '—', k => repLabel(k), measure, TOP_N),
    [currentRows, measure],
  );
  const byRepAll = useMemo(
    () => breakdown(currentRows, r => r.created_by ?? '—', k => repLabel(k), measure),
    [currentRows, measure],
  );
  const byFamily = useMemo(
    () => breakdown(currentRows, r => r.family ?? '', k => familyLabel(k), measure, TOP_N),
    [currentRows, measure],
  );
  const byFamilyAll = useMemo(
    () => breakdown(currentRows, r => r.family ?? '', k => familyLabel(k), measure),
    [currentRows, measure],
  );

  const activeFilters = [
    storeFilter && { label: `Store: ${storeLabel(storeFilter)}`, clear: () => setStoreFilter('') },
    familyFilter && { label: `Range: ${familyLabel(familyFilter)}`, clear: () => setFamilyFilter('') },
    modelFilter && { label: `Model: ${options.models.find(([k]) => k === modelFilter)?.[1] ?? modelFilter}`, clear: () => setModelFilter('') },
    channelFilter && { label: `Channel: ${channelFilter === 'pos' ? 'POS' : 'Staff'}`, clear: () => setChannelFilter('') },
    repFilter && { label: `Sold by: ${repLabel(repFilter)}`, clear: () => setRepFilter('') },
    search.trim() && { label: `Search: ${search.trim()}`, clear: () => setSearch('') },
  ].filter(Boolean) as { label: string; clear: () => void }[];

  const compLabel = comparisonLabel(range, compare);

  const exportCsv = () => {
    const header = [
      'Entry No', 'Posting Date', 'Document No', 'Document Type', 'Item No', 'Description',
      'Units', 'Revenue', 'COGS', 'Margin', 'Store', 'Store Code', 'Location', 'Serial No', 'Assembly Order',
      'Created By', 'Channel',
    ];
    const escape = (v: unknown) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(',')];
    for (const r of currentRows) {
      lines.push([
        r.entry_no, r.posting_date, r.document_no, r.document_type ?? '', r.item_no, r.description ?? '',
        r.units, r.revenue, r.cogs, Math.round((Number(r.revenue) - Number(r.cogs)) * 100) / 100,
        storeOf(r).name, r.store_code ?? '', r.location_code, r.serial_no ?? '', lookupAso(r.serial_no)?.order_number ?? '',
        r.created_by ?? '', r.channel,
      ].map(escape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-${range.from}-to-${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const PAGE_SIZE = 50;
  const pageCount = Math.max(1, Math.ceil(currentRows.length / PAGE_SIZE));
  const page = Math.min(tablePage, pageCount);
  const pageRows = currentRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Sales Report</h1>
          <p className="text-sm text-muted-foreground">
            PC sales posted in Business Central
            {bounds?.earliest && bounds?.latest && (
              <> · {formatISODate(bounds.earliest)} – {formatISODate(bounds.latest)} on record</>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {/* The sell-through tab exports its own table from its own toolbar. */}
          {tab === 'sales' && (
            <Button variant="outline" onClick={exportCsv} disabled={currentRows.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          )}
          <Button onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import from BC
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b flex gap-1">
        {([
          { value: 'sales', label: 'Sales' },
          { value: 'sellThrough', label: 'Sell-through vs stock' },
        ] as const).map(t => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            aria-current={tab === t.value ? 'page' : undefined}
            className={cn(
              'px-3 py-2 text-sm -mb-px border-b-2 transition-colors',
              tab === t.value
                ? 'border-primary text-foreground font-medium'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters — one row, scopes every chart, stat and table below */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <select
              className={SELECT_CLASS}
              value={preset}
              onChange={e => { setPreset(e.target.value as RangePreset); setTablePage(1); }}
            >
              {RANGE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>

            {preset === 'custom' && (
              <>
                <Input
                  type="date"
                  className="h-9 w-[150px]"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={e => { setCustomFrom(e.target.value); setTablePage(1); }}
                />
                <span className="text-sm text-muted-foreground">to</span>
                <Input
                  type="date"
                  className="h-9 w-[150px]"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={e => { setCustomTo(e.target.value); setTablePage(1); }}
                />
              </>
            )}

            {/* Everything from here to the search box reads sales_entries only.
                The sell-through tab is built from build records, which carry no
                location, seller or channel — showing the controls there would
                imply a filter that silently does nothing. */}
            {tab === 'sales' && (
              <>
              <select className={SELECT_CLASS} value={granularity} onChange={e => setGranularity(e.target.value as 'auto' | Granularity)}>
                {GRANULARITIES.map(g => (
                  <option key={g.value} value={g.value}>
                    {g.value === 'auto' ? `Auto (${effectiveGranularity})` : g.label}
                  </option>
                ))}
              </select>

              <select className={SELECT_CLASS} value={compare} onChange={e => setCompare(e.target.value as ComparisonMode)}>
                {COMPARISONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>

              <select className={SELECT_CLASS} value={measure} onChange={e => setMeasure(e.target.value as Measure)}>
                {MEASURES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>

              <div className="h-6 w-px bg-border" aria-hidden />

              <select className={SELECT_CLASS} value={storeFilter} onChange={e => { setStoreFilter(e.target.value); setTablePage(1); }}>
                <option value="">All stores</option>
                {options.storeGroups.map(g => (
                  <optgroup key={g.group} label={g.group}>
                    {g.stores.map(s => <option key={s.name} value={s.name}>{storeDisplay(s)}</option>)}
                  </optgroup>
                ))}
              </select>

              <select className={SELECT_CLASS} value={familyFilter} onChange={e => { setFamilyFilter(e.target.value); setTablePage(1); }}>
                <option value="">All ranges</option>
                {options.families.map(f => <option key={f} value={f}>{familyLabel(f)}</option>)}
              </select>

              {/* Capped — the longest option (a bundle SKU) would otherwise stretch the row. */}
              <select
                className={cn(SELECT_CLASS, 'max-w-[240px]')}
                value={modelFilter}
                onChange={e => { setModelFilter(e.target.value); setTablePage(1); }}
              >
                <option value="">All models</option>
                {options.models.map(([sku, label]) => <option key={sku} value={sku}>{label} ({sku})</option>)}
              </select>

              <select className={SELECT_CLASS} value={channelFilter} onChange={e => { setChannelFilter(e.target.value); setTablePage(1); }}>
                <option value="">POS + staff</option>
                <option value="pos">POS only</option>
                <option value="staff">Staff only</option>
              </select>

              <select className={SELECT_CLASS} value={repFilter} onChange={e => { setRepFilter(e.target.value); setTablePage(1); }}>
                <option value="">All sellers</option>
                {options.reps.map(r => <option key={r} value={r}>{repLabel(r)}</option>)}
              </select>

              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="h-9 pl-8 w-[200px]"
                  placeholder="Serial, document, SKU…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setTablePage(1); }}
                />
              </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-2 items-center text-xs text-muted-foreground">
            <span>
              {formatISODate(range.from)} – {formatISODate(range.to)}
              {' · '}{daysInclusive(range.from, range.to)} days
              {tab === 'sales' && compRange && <> · compared with {formatISODate(compRange.from)} – {formatISODate(compRange.to)}</>}
              {tab === 'sales' && comparisonUnavailable && <> · no comparison — nothing imported before {formatISODate(bounds!.earliest!)}</>}
            </span>
            {tab === 'sales' && activeFilters.map((f, i) => (
              <Badge key={i} variant="secondary" className="gap-1 font-normal">
                {f.label}
                <button onClick={f.clear} className="hover:text-foreground" aria-label={`Clear ${f.label}`}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {tab === 'sales' ? (
        <>
        {error && (
          <Card>
            <CardContent className="py-8 text-center text-sm text-destructive">
              Could not load sales entries: {error instanceof Error ? error.message : 'unknown error'}
            </CardContent>
          </Card>
        )}

        {isLoading && !allRows ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          // Refetch holds the previous render at reduced opacity — no skeleton flash.
          <div className={cn('space-y-6 transition-opacity', isPlaceholderData && 'opacity-60')}>
            {/* KPI row */}
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
              <StatTile
                label="Units sold"
                value={totals.units.toLocaleString()}
                delta={compare === 'none' ? null : pctDelta(totals.units, priorTotals.units)}
                deltaLabel={compLabel}
                priorValue={compRange ? priorTotals.units.toLocaleString() : null}
                footnote={totals.returnUnits > 0 ? `net of ${totals.returnUnits} return${totals.returnUnits > 1 ? 's' : ''}` : undefined}
              />
              {/* Its own metric, deliberately not folded into units sold. */}
              <StatTile
                label="Revival packs"
                value={totals.packUnits.toLocaleString()}
                delta={compare === 'none' ? null : pctDelta(totals.packUnits, priorTotals.packUnits)}
                deltaLabel={compLabel}
                priorValue={compRange ? priorTotals.packUnits.toLocaleString() : null}
                footnote="not counted as PCs"
              />
              <StatTile
                label="Revenue"
                value={formatCurrency(totals.revenue)}
                delta={compare === 'none' ? null : pctDelta(totals.revenue, priorTotals.revenue)}
                deltaLabel={compLabel}
                priorValue={compRange ? formatCurrency(priorTotals.revenue) : null}
              />
              <StatTile
                label="Gross margin"
                value={formatCurrency(totals.margin)}
                delta={compare === 'none' ? null : pctDelta(totals.margin, priorTotals.margin)}
                deltaLabel={compLabel}
                priorValue={compRange ? formatCurrency(priorTotals.margin) : null}
                footnote={totals.marginPct != null ? `${totals.marginPct.toFixed(1)}% of revenue` : undefined}
              />
              <StatTile
                label="Average selling price"
                value={totals.asp != null ? formatCurrency(totals.asp) : '—'}
                delta={compare === 'none' || priorTotals.asp == null || totals.asp == null
                  ? null
                  : pctDelta(totals.asp, priorTotals.asp)}
                deltaLabel={compLabel}
                priorValue={compRange && priorTotals.asp != null ? formatCurrency(priorTotals.asp) : null}
              />
            </div>

            {/* Trend */}
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <div>
                  <CardTitle className="text-base">
                    {MEASURES.find(m => m.value === measure)?.label} over time
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    By {effectiveGranularity}
                    {compRange && ` · compared with ${compare === 'yoy' ? 'last year' : 'the previous period'}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {trendView === 'chart' && (
                    <ChartTypeToggle value={trendChartType ?? defaultTrendChart} onChange={setTrendChartType} />
                  )}
                  <ViewToggle value={trendView} onChange={setTrendView} />
                </div>
              </CardHeader>
              <CardContent>
                {currentRows.length === 0 ? (
                  <EmptyState />
                ) : trendView === 'chart' ? (
                  <TrendChart
                    series={series}
                    measure={measure}
                    hasComparison={!!compRange}
                    compare={compare}
                    chartType={trendChartType ?? defaultTrendChart}
                  />
                ) : (
                  <TrendTable series={series} hasComparison={!!compRange} compare={compare} />
                )}
              </CardContent>
            </Card>

            {/* Breakdowns */}
            <div className="grid gap-4 lg:grid-cols-2">
              <BreakdownCard
                title="By model"
                subtitle="Grouped by BC item number — the stable key, since item descriptions get renamed"
                data={byModel}
                fullData={byModelAll}
                measure={measure}
                onSelect={setModelFilter}
              />
              <BreakdownCard
                title="By store"
                subtitle="BC Store dimension — who the sale is credited to, not where the stock shipped from"
                data={byStore}
                fullData={byStoreAll}
                measure={measure}
                onSelect={setStoreFilter}
              />
              <BreakdownCard
                title="By seller"
                subtitle="POS terminals and named staff logins, from the BC Created By field"
                data={byRep}
                fullData={byRepAll}
                measure={measure}
                onSelect={setRepFilter}
              />
              <BreakdownCard
                title="By range"
                subtitle="Parsed from the SKU (AOI-<range><platform>-…)"
                data={byFamily}
                fullData={byFamilyAll}
                measure={measure}
                onSelect={setFamilyFilter}
              />
            </div>

            {/* Detail table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  Entries
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    {currentRows.length.toLocaleString()} row{currentRows.length === 1 ? '' : 's'}
                  </span>
                </CardTitle>
                {currentRows.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {linkStats.linked.toLocaleString()} linked to an assembly order
                    {linkStats.preTracking > 0 && ` · ${linkStats.preTracking.toLocaleString()} predate serial tracking`}
                    {linkStats.noSerial > 0 && ` · ${linkStats.noSerial.toLocaleString()} with no serial`}
                    {linkStats.unmatched > 0 && ` · ${linkStats.unmatched.toLocaleString()} serial not found`}
                  </p>
                )}
              </CardHeader>
              <CardContent>
                {currentRows.length === 0 ? (
                  <EmptyState />
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-xs text-muted-foreground text-left">
                            <th className="py-2 pr-3 font-medium">Date</th>
                            <th className="py-2 pr-3 font-medium">Document</th>
                            <th className="py-2 pr-3 font-medium">SKU</th>
                            <th className="py-2 pr-3 font-medium">Model</th>
                            <th className="py-2 pr-3 font-medium text-right">Units</th>
                            <th className="py-2 pr-3 font-medium text-right">Revenue</th>
                            <th className="py-2 pr-3 font-medium text-right">Cost</th>
                            <th className="py-2 pr-3 font-medium text-right">Margin</th>
                            <th className="py-2 pr-3 font-medium">Store</th>
                            <th className="py-2 pr-3 font-medium">Serial</th>
                            <th className="py-2 pr-3 font-medium">Assembly Order</th>
                            <th className="py-2 font-medium">Sold by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.map(r => {
                            const margin = Math.round((Number(r.revenue) - Number(r.cogs)) * 100) / 100;
                            return (
                              <tr key={r.entry_no} className="border-b last:border-0 hover:bg-muted/40">
                                <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{formatISODate(r.posting_date)}</td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  {r.document_no}
                                  {r.is_return && <Badge variant="outline" className="ml-2 text-xs">Return</Badge>}
                                </td>
                                <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{r.item_no}</td>
                                <td className="py-2 pr-3">{truncate(r.description?.split(' - ')[0] ?? '—', 28)}</td>
                                <td className="py-2 pr-3 text-right tabular-nums">{r.units}</td>
                                <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(r.revenue)}</td>
                                <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(r.cogs)}</td>
                                <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(margin)}</td>
                                <td className="py-2 pr-3 whitespace-nowrap" title={`Store code ${r.store_code ?? '—'}`}>
                                  {storeOf(r).name}
                                  <span className="block text-xs text-muted-foreground">{r.location_code}</span>
                                </td>
                                <td className="py-2 pr-3 font-mono text-xs">{r.serial_no ?? '—'}</td>
                                <td className="py-2 pr-3 whitespace-nowrap">
                                  <AsoCell row={r} aso={lookupAso(r.serial_no)} trackingFrom={trackingFrom} />
                                </td>
                                <td className="py-2 whitespace-nowrap">{repLabel(r.created_by)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {pageCount > 1 && (
                      <div className="flex items-center justify-between pt-3 text-sm">
                        <span className="text-muted-foreground">Page {page} of {pageCount}</span>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setTablePage(page - 1)}>
                            Previous
                          </Button>
                          <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setTablePage(page + 1)}>
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        </>
      ) : (
        <SellThroughPanel range={range} />
      )}

      <SalesImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}

// ── Pieces ───────────────────────────────────────────────────────────────────

/**
 * The assembly order behind one sale, matched on serial number.
 *
 * The "no link" states are kept distinct on purpose — they need different fixes.
 * A sale predating serial tracking can never link and is not a defect; an
 * unmatched serial after that means the unit wasn't built through the Hub or the
 * serial is mistyped; a missing serial is a sales-process problem; and an
 * ambiguous one means two orders claim the same serial.
 */
function AsoCell({
  row,
  aso,
  trackingFrom,
}: {
  row: SalesRow;
  aso: AssemblyOrderRef | null;
  trackingFrom: string | null;
}) {
  if (aso) {
    // A SKU mismatch doesn't invalidate the match - serial is the stronger key -
    // but it's worth surfacing, since it usually means a BC item rename.
    const skuMismatch = aso.build_sku && aso.build_sku !== row.item_no;
    return (
      <span className="inline-flex items-center gap-1.5">
        <Link
          to={`/stock-io/${aso.id}`}
          className="font-mono text-xs text-primary hover:underline"
        >
          {aso.order_number}
        </Link>
        {aso.quantity > 1 && (
          <span className="text-xs text-muted-foreground">u{aso.unit}/{aso.quantity}</span>
        )}
        {aso.ambiguous && (
          <Badge variant="outline" className="text-xs" title="More than one assembly order carries this serial">
            Ambiguous
          </Badge>
        )}
        {skuMismatch && (
          <Badge
            variant="outline"
            className="text-xs"
            title={`Assembly order is SKU ${aso.build_sku}, the sale is ${row.item_no}`}
          >
            SKU differs
          </Badge>
        )}
      </span>
    );
  }
  if (!serialKey(row.serial_no)) {
    return (
      <span
        className="text-xs text-muted-foreground italic"
        title={
          row.serial_no
            ? `Serial reads "${row.serial_no}" — a placeholder, not a real serial, so it can't be linked`
            : "No serial on the sale, so it can't be linked to an assembly order"
        }
      >
        no serial
      </span>
    );
  }
  if (trackingFrom && row.posting_date < trackingFrom) {
    return (
      <span
        className="text-xs text-muted-foreground/70 italic"
        title={`Sold before the Hub recorded serials on assembly orders (from ${trackingFrom}) — this can never link`}
      >
        pre-tracking
      </span>
    );
  }
  return (
    <span className="text-xs text-muted-foreground" title="No assembly order carries this serial">
      not found
    </span>
  );
}

function EmptyState() {
  return (
    <div className="py-12 text-center">
      <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
      <p className="text-sm text-muted-foreground">No sales entries match the current filters.</p>
    </div>
  );
}

function ViewToggle({ value, onChange }: { value: 'chart' | 'table'; onChange: (v: 'chart' | 'table') => void }) {
  return (
    <div className="flex rounded-md border overflow-hidden">
      {(['chart', 'table'] as const).map(v => (
        <button
          key={v}
          onClick={() => onChange(v)}
          aria-pressed={value === v}
          className={cn(
            'px-2 py-1 text-xs flex items-center gap-1 transition-colors',
            value === v ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted/50',
          )}
        >
          {v === 'chart' ? <BarChart3 className="h-3.5 w-3.5" /> : <TableIcon className="h-3.5 w-3.5" />}
          {v === 'chart' ? 'Chart' : 'Table'}
        </button>
      ))}
    </div>
  );
}

const CHART_TYPES: { value: TrendChartType; label: string; Icon: typeof BarChart3 }[] = [
  { value: 'line', label: 'Line', Icon: LineChartIcon },
  { value: 'bar', label: 'Bar', Icon: BarChart3 },
  { value: 'area', label: 'Area', Icon: AreaChartIcon },
];

function ChartTypeToggle({
  value, onChange,
}: {
  value: TrendChartType;
  onChange: (v: TrendChartType) => void;
}) {
  return (
    <div className="flex rounded-md border overflow-hidden" role="group" aria-label="Chart type">
      {CHART_TYPES.map(({ value: v, label, Icon }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          aria-pressed={value === v}
          title={`${label} chart`}
          className={cn(
            'px-2 py-1 text-xs flex items-center gap-1 transition-colors',
            value === v ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted/50',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          <span className="sr-only sm:not-sr-only">{label}</span>
        </button>
      ))}
    </div>
  );
}

function StatTile({
  label, value, delta, deltaLabel, priorValue, footnote,
}: {
  label: string;
  value: string;
  delta: number | null;
  deltaLabel: string;
  /** The comparison period's own figure. null when there is no comparison. */
  priorValue?: string | null;
  footnote?: string;
}) {
  const Icon = delta == null ? Minus : delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus;
  // Up is good for every measure on this tile row (units, revenue, margin, ASP).
  const color = delta == null || delta === 0
    ? 'var(--ink-muted)'
    : delta > 0 ? 'var(--delta-good)' : 'var(--delta-bad)';
  // A percentage on its own hides the baseline it was measured against, so the
  // prior figure is spelled out whenever a comparison period is in play —
  // including when the delta itself is undefined because that baseline was zero.
  const hasPrior = priorValue != null;

  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground">{label}</p>
        {/* Proportional figures — tabular-nums makes a large standalone number look loose. */}
        <p className="text-2xl font-semibold mt-1">{value}</p>
        <div className="flex items-center gap-1 mt-1 text-xs">
          {delta != null ? (
            <>
              <Icon className="h-3.5 w-3.5" style={{ color }} aria-hidden />
              <span style={{ color }}>{delta > 0 ? '+' : ''}{delta.toFixed(1)}%</span>
              <span className="text-muted-foreground">{deltaLabel}</span>
            </>
          ) : hasPrior ? (
            <>
              <Minus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
              <span className="text-muted-foreground">{deltaLabel}</span>
            </>
          ) : (
            <span className="text-muted-foreground">
              {footnote ?? 'no comparable period'}
            </span>
          )}
        </div>
        {hasPrior && (
          <p className="text-xs text-muted-foreground mt-0.5">
            from <span className="tabular-nums">{priorValue}</span>
          </p>
        )}
        {footnote && (delta != null || hasPrior) && (
          <p className="text-xs text-muted-foreground mt-0.5">{footnote}</p>
        )}
      </CardContent>
    </Card>
  );
}

function measureValue(p: { revenue: number; units: number; margin: number }, m: Measure): number {
  return m === 'revenue' ? p.revenue : m === 'units' ? p.units : p.margin;
}

function formatMeasure(v: number, m: Measure): string {
  return m === 'units' ? v.toLocaleString() : formatCurrency(v);
}

function formatMeasureCompact(v: number, m: Measure): string {
  return m === 'units' ? v.toLocaleString() : compactCurrency(v);
}

const AXIS_STYLE = { fontSize: 11, fill: 'var(--ink-muted)' } as const;

function TrendChart({
  series, measure, hasComparison, compare, chartType,
}: {
  series: SeriesPoint[];
  measure: Measure;
  hasComparison: boolean;
  compare: ComparisonMode;
  chartType: TrendChartType;
}) {
  const data = series.map(p => ({
    ...p,
    value: measureValue(p, measure),
    prior: p.priorRevenue == null ? null : measureValue(
      { revenue: p.priorRevenue, units: p.priorUnits ?? 0, margin: p.priorMargin ?? 0 },
      measure,
    ),
  }));

  const priorName = compare === 'yoy' ? 'Last year' : 'Previous period';

  // Emphasis: the current period carries the accent, the comparison recedes.
  // The prior series never gets a fill — two washes over each other read as a
  // third colour — so in area mode it stays a line.
  const priorSeries = hasComparison ? (
    chartType === 'bar' ? (
      <Bar
        key="prior"
        name={priorName}
        dataKey="prior"
        fill="var(--series-muted)"
        maxBarSize={24}
        radius={[4, 4, 0, 0]}
      />
    ) : (
      <Line
        key="prior"
        name={priorName}
        type="linear"
        dataKey="prior"
        stroke="var(--series-muted)"
        strokeWidth={2}
        dot={false}
        activeDot={{ r: 4, strokeWidth: 2, stroke: 'hsl(var(--card))' }}
        connectNulls
      />
    )
  ) : null;

  const currentName = 'Selected period';
  const currentSeries =
    chartType === 'bar' ? (
      <Bar
        key="value"
        name={currentName}
        dataKey="value"
        fill="var(--series-1)"
        maxBarSize={24}
        radius={[4, 4, 0, 0]}
      />
    ) : chartType === 'area' ? (
      <Area
        key="value"
        name={currentName}
        type="linear"
        dataKey="value"
        stroke="var(--series-1)"
        strokeWidth={2}
        fill="var(--series-1)"
        fillOpacity={0.1}
        dot={{ r: 4, strokeWidth: 2, stroke: 'hsl(var(--card))', fill: 'var(--series-1)' }}
        activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--card))' }}
      />
    ) : (
      <Line
        key="value"
        name={currentName}
        type="linear"
        dataKey="value"
        stroke="var(--series-1)"
        strokeWidth={2}
        dot={{ r: 4, strokeWidth: 2, stroke: 'hsl(var(--card))', fill: 'var(--series-1)' }}
        activeDot={{ r: 5, strokeWidth: 2, stroke: 'hsl(var(--card))' }}
      />
    );

  return (
    // Height includes the x-axis band so the labels are never cut off.
    <ResponsiveContainer width="100%" height={320}>
      {/* One composed chart so the axes, grid and tooltip are defined once and
          only the marks change with the toggle. barGap is the 2px surface gap
          that separates a pair of grouped bars. */}
      <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 8 }} barGap={2}>
        <CartesianGrid stroke="var(--grid-line)" strokeWidth={1} vertical={false} />
        <XAxis dataKey="label" tick={AXIS_STYLE} tickLine={false} axisLine={{ stroke: 'var(--axis-line)' }} />
        <YAxis
          tick={AXIS_STYLE}
          tickLine={false}
          axisLine={false}
          width={64}
          tickFormatter={(v: number) => formatMeasureCompact(v, measure)}
        />
        <Tooltip
          content={<TrendTooltip measure={measure} priorName={priorName} />}
          cursor={chartType === 'bar'
            ? { fill: 'hsl(var(--muted))', fillOpacity: 0.4 }
            : { stroke: 'var(--axis-line)', strokeWidth: 1 }}
        />
        {/* Two series need a legend; a single series is already named by the card title. */}
        {hasComparison && (
          <Legend
            verticalAlign="top"
            align="right"
            height={28}
            iconType={chartType === 'bar' ? 'rect' : 'plainline'}
            wrapperStyle={{ fontSize: 12 }}
          />
        )}
        {/* Prior first so the current period draws on top of it. */}
        {priorSeries}
        {currentSeries}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

interface TooltipPayloadItem {
  payload: SeriesPoint & { value: number; prior: number | null };
}

function TrendTooltip({
  active, payload, measure, priorName,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  measure: Measure;
  priorName: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0].payload;

  return (
    <div className="rounded-md border bg-popover text-popover-foreground shadow-md px-3 py-2 text-xs space-y-1.5">
      <p className="font-medium">{p.longLabel}</p>
      <TooltipRow color="var(--series-1)" label="Selected period" value={formatMeasure(p.value, measure)} />
      {p.prior != null && (
        <TooltipRow color="var(--series-muted)" label={priorName} value={formatMeasure(p.prior, measure)} />
      )}
      <div className="pt-1 border-t space-y-0.5 text-muted-foreground">
        <p>{p.units} unit{p.units === 1 ? '' : 's'} · {formatCurrency(p.revenue)} revenue</p>
        <p>{formatCurrency(p.margin)} margin{p.marginPct != null && ` (${p.marginPct.toFixed(1)}%)`}</p>
      </div>
    </div>
  );
}

/** Line-key + label; the value leads in weight because the reader already has the series. */
function TooltipRow({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block rounded-full" style={{ width: 12, height: 2, background: color }} aria-hidden />
      <span className="font-medium">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

function TrendTable({
  series, hasComparison, compare,
}: {
  series: SeriesPoint[];
  hasComparison: boolean;
  compare: ComparisonMode;
}) {
  // Every measure gets its comparison column, not just revenue — the prior
  // period's units and margin are as much a part of the answer as its money.
  const priorPrefix = compare === 'yoy' ? 'LY' : 'Prior';

  return (
    <div className="overflow-x-auto max-h-[320px]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card">
          <tr className="border-b text-xs text-muted-foreground text-left">
            <th className="py-2 pr-3 font-medium">Period</th>
            <th className="py-2 pr-3 font-medium text-right">Units</th>
            {hasComparison && <th className="py-2 pr-3 font-medium text-right whitespace-nowrap">{priorPrefix} units</th>}
            <th className="py-2 pr-3 font-medium text-right">Revenue</th>
            {hasComparison && <th className="py-2 pr-3 font-medium text-right whitespace-nowrap">{priorPrefix} revenue</th>}
            <th className="py-2 pr-3 font-medium text-right">Margin</th>
            {hasComparison && <th className="py-2 pr-3 font-medium text-right whitespace-nowrap">{priorPrefix} margin</th>}
            <th className="py-2 font-medium text-right">Margin %</th>
          </tr>
        </thead>
        <tbody>
          {series.map(p => (
            <tr key={p.key} className="border-b last:border-0">
              <td className="py-2 pr-3 whitespace-nowrap">{p.longLabel}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{p.units}</td>
              {hasComparison && (
                <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                  {p.priorUnits != null ? p.priorUnits : '—'}
                </td>
              )}
              <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(p.revenue)}</td>
              {hasComparison && (
                <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                  {p.priorRevenue != null ? formatCurrency(p.priorRevenue) : '—'}
                </td>
              )}
              <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(p.margin)}</td>
              {hasComparison && (
                <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                  {p.priorMargin != null ? formatCurrency(p.priorMargin) : '—'}
                </td>
              )}
              <td className="py-2 text-right tabular-nums">{p.marginPct != null ? `${p.marginPct.toFixed(1)}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BreakdownCard({
  title, subtitle, data, fullData, measure, onSelect,
}: {
  title: string;
  subtitle?: string;
  data: Breakdown[];
  /** Uncapped, no "Other" row — what the show-all dialog lists. */
  fullData: Breakdown[];
  measure: Measure;
  onSelect: (key: string) => void;
}) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [showAll, setShowAll] = useState(false);
  const chartData = data.map(d => ({ ...d, value: measureValue(d, measure) }));

  // "Other" is a fold, not a category — selecting it as a filter is meaningless,
  // so it opens the full list instead.
  const handleSelect = (key: string) => {
    if (key === OTHER_KEY) setShowAll(true);
    else onSelect(key);
  };
  // Enough height for every bar plus the axis band, so the card never scrolls internally.
  const height = Math.max(160, chartData.length * 34 + 32);

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0 pb-2 gap-3">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <ViewToggle value={view} onChange={setView} />
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <EmptyState />
        ) : view === 'chart' ? (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 72, bottom: 4, left: 4 }}>
              <CartesianGrid stroke="var(--grid-line)" strokeWidth={1} horizontal={false} />
              <XAxis
                type="number"
                tick={AXIS_STYLE}
                tickLine={false}
                axisLine={{ stroke: 'var(--axis-line)' }}
                tickFormatter={(v: number) => formatMeasureCompact(v, measure)}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={AXIS_STYLE}
                tickLine={false}
                axisLine={false}
                width={140}
                tickFormatter={(v: string) => truncate(v, 20)}
              />
              <Tooltip content={<BreakdownTooltip measure={measure} />} cursor={{ fill: 'hsl(var(--muted))', fillOpacity: 0.4 }} />
              {/* One series, one color — never a value ramp on nominal categories. */}
              <Bar
                dataKey="value"
                fill="var(--series-1)"
                barSize={20}
                radius={[0, 4, 4, 0]}
                onClick={(d: { key?: string }) => d.key && handleSelect(d.key)}
                className="cursor-pointer"
              >
                {/* Value at the tip, outside the bar, so it is never clipped. */}
                <LabelList
                  dataKey="value"
                  position="right"
                  offset={8}
                  style={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                  formatter={(v: number) => formatMeasureCompact(v, measure)}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground text-left">
                  <th className="py-2 pr-3 font-medium">{title.replace('By ', '')}</th>
                  <th className="py-2 pr-3 font-medium text-right">Units</th>
                  <th className="py-2 pr-3 font-medium text-right">Revenue</th>
                  <th className="py-2 pr-3 font-medium text-right">Margin</th>
                  <th className="py-2 font-medium text-right">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {data.map(d => (
                  <tr
                    key={d.key}
                    className="border-b last:border-0 cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSelect(d.key)}
                  >
                    <td className="py-2 pr-3">
                      {d.label}
                      {d.key === OTHER_KEY && (
                        <span className="ml-1.5 text-xs text-primary">show all</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {d.units}
                      {d.packUnits !== 0 && (
                        <span className="ml-1 text-xs text-muted-foreground">+{d.packUnits} pk</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(d.revenue)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(d.margin)}</td>
                    <td className="py-2 text-right tabular-nums">{d.marginPct != null ? `${d.marginPct.toFixed(1)}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Only offered when there is actually a folded tail to expand. */}
        {data.some(d => d.key === OTHER_KEY) && (
          <button
            className="mt-3 text-xs text-primary hover:underline"
            onClick={() => setShowAll(true)}
          >
            Show all {fullData.length}
          </button>
        )}
      </CardContent>

      <BreakdownAllDialog
        open={showAll}
        onClose={() => setShowAll(false)}
        title={title}
        data={fullData}
        onSelect={(key) => { setShowAll(false); onSelect(key); }}
      />
    </Card>
  );
}

/** Every group for one dimension, ungrouped and scrollable. */
function BreakdownAllDialog({
  open, onClose, title, data, onSelect,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  data: Breakdown[];
  onSelect: (key: string) => void;
}) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const rows = q ? data.filter(d => d.label.toLowerCase().includes(q) || d.key.toLowerCase().includes(q)) : data;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setSearch(''); onClose(); } }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            {title} — all {data.length}
          </DialogTitle>
        </DialogHeader>

        <Input
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-sm"
        />

        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b text-xs text-muted-foreground text-left">
                <th className="py-2 pr-3 font-medium">{title.replace('By ', '')}</th>
                <th className="py-2 pr-3 font-medium text-right">Units</th>
                <th className="py-2 pr-3 font-medium text-right">Revenue</th>
                <th className="py-2 pr-3 font-medium text-right">Margin</th>
                <th className="py-2 font-medium text-right">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(d => (
                <tr
                  key={d.key}
                  className="border-b last:border-0 cursor-pointer hover:bg-muted/50"
                  onClick={() => onSelect(d.key)}
                  title={`Filter the report by ${d.label}`}
                >
                  <td className="py-2 pr-3">{d.label}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {d.units}
                    {d.packUnits !== 0 && (
                      <span className="ml-1 text-xs text-muted-foreground">+{d.packUnits} pk</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(d.revenue)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{formatCurrency(d.margin)}</td>
                  <td className="py-2 text-right tabular-nums">{d.marginPct != null ? `${d.marginPct.toFixed(1)}%` : '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                    Nothing matches "{search}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BreakdownTooltip({
  active, payload, measure,
}: {
  active?: boolean;
  payload?: { payload: Breakdown & { value: number } }[];
  measure: Measure;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-md border bg-popover text-popover-foreground shadow-md px-3 py-2 text-xs space-y-1">
      <p className="font-medium">{d.label}</p>
      <TooltipRow color="var(--series-1)" label={MEASURES.find(m => m.value === measure)!.label} value={formatMeasure(d.value, measure)} />
      <div className="pt-1 border-t text-muted-foreground space-y-0.5">
        <p>{d.units} unit{d.units === 1 ? '' : 's'} · {formatCurrency(d.revenue)} revenue</p>
        <p>{formatCurrency(d.margin)} margin{d.marginPct != null && ` (${d.marginPct.toFixed(1)}%)`}</p>
      </div>
    </div>
  );
}
